import React, { useState, useEffect } from "react";
import UserManagementPage from "../../../entities/SuperAdminPage/UserManagement";
import { formatDate as sharedFormatDate } from "../../../utils/DateFormat";
import { subscriptionService } from "../../../api";
import { ApiConfig } from "../../../api/ApiConfig";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";

const PAYSTACK_PAYMENT_CONTEXT_KEY = "pendingPaystackSubscriptionPayment";
const PAYMENT_CONTEXT_MAX_AGE_MS = 1000 * 60 * 60;
const PAYMENT_PARAM_KEYS = [
  "status",
  "paymentStatus",
  "payment_status",
  "success",
  "message",
  "error",
  "reference",
  "trxref",
  "paymentMessage",
  "paymentError",
];
const PAYMENT_FAILURE_TOKENS = [
  "fail",
  "failed",
  "error",
  "cancel",
  "cancelled",
  "declined",
  "abandon",
  "unsuccessful",
];
const PAYMENT_SUCCESS_VALUES = new Set([
  "success",
  "successful",
  "true",
  "paid",
  "completed",
  "complete",
]);
const BACKEND_ORIGIN = (() => {
  try {
    return new URL(ApiConfig.baseApi).origin.toLowerCase();
  } catch (error) {
    return "";
  }
})();

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.subscriptions)) return response.data.subscriptions;
  if (Array.isArray(response?.subscriptions)) return response.subscriptions;
  if (Array.isArray(response?.result)) return response.result;
  return [];
};

const extractTotalCount = (response, fallback = 0) => {
  const candidates = [
    response?.data?.totalCount,
    response?.data?.total,
    response?.data?.count,
    response?.totalCount,
    response?.total,
    response?.count,
  ];

  const total = candidates.find((value) => typeof value === "number");
  return typeof total === "number" ? total : fallback;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getQueryValue = (searchParams, keys = []) => {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  return "";
};

const readPendingPaymentContext = () => {
  try {
    const raw = sessionStorage.getItem(PAYSTACK_PAYMENT_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    const isStale = !createdAt || Date.now() - createdAt > PAYMENT_CONTEXT_MAX_AGE_MS;

    if (isStale) {
      sessionStorage.removeItem(PAYSTACK_PAYMENT_CONTEXT_KEY);
      return null;
    }

    return {
      reference: String(parsed?.reference || "").trim(),
      createdAt,
    };
  } catch (error) {
    sessionStorage.removeItem(PAYSTACK_PAYMENT_CONTEXT_KEY);
    return null;
  }
};

const clearPaymentQueryParams = () => {
  try {
    const url = new URL(window.location.href);
    let hasChange = false;

    PAYMENT_PARAM_KEYS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        hasChange = true;
      }
    });

    if (!hasChange) return;

    const nextSearch = url.searchParams.toString();
    const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash || ""}`;
    window.history.replaceState({}, "", nextUrl);
  } catch (error) {
    // No-op: URL cleanup should not break the page.
  }
};

const inferPaymentStatus = ({ statusValue, errorValue, messageValue, hasReference }) => {
  const normalizedStatus = normalizeText(statusValue);
  const combinedText = normalizeText(
    `${statusValue || ""} ${errorValue || ""} ${messageValue || ""}`
  );
  const hasFailureSignal = PAYMENT_FAILURE_TOKENS.some((token) =>
    combinedText.includes(token)
  );

  if (hasFailureSignal) return "error";
  if (PAYMENT_SUCCESS_VALUES.has(normalizedStatus)) return "success";
  if (hasReference) return "success";
  return null;
};

const buildPaymentModalPayload = () => {
  const searchParams = new URLSearchParams(window.location.search || "");
  const hasPaymentParams = PAYMENT_PARAM_KEYS.some((key) => searchParams.has(key));
  const pendingContext = readPendingPaymentContext();
  const referrer = String(document.referrer || "").toLowerCase();

  const statusValue = getQueryValue(searchParams, [
    "status",
    "paymentStatus",
    "payment_status",
    "success",
  ]);
  const messageValue = getQueryValue(searchParams, ["message", "paymentMessage"]);
  const errorValue = getQueryValue(searchParams, ["error", "paymentError"]);
  const referenceValue = getQueryValue(searchParams, ["reference", "trxref"]);
  const finalReference = referenceValue || pendingContext?.reference || "";

  const inferredStatus = inferPaymentStatus({
    statusValue,
    errorValue,
    messageValue,
    hasReference: Boolean(finalReference),
  });

  const cameFromPaymentReferrer =
    referrer.includes("checkout.paystack.com") ||
    referrer.includes("payments/paystack/callback") ||
    referrer.includes("paystack") ||
    (BACKEND_ORIGIN && referrer.includes(BACKEND_ORIGIN));

  const shouldUsePendingContextFallback =
    Boolean(pendingContext) && !hasPaymentParams && !inferredStatus && cameFromPaymentReferrer;

  if (!inferredStatus && !shouldUsePendingContextFallback) {
    return null;
  }

  const status = inferredStatus || "success";
  const fallbackMessage =
    status === "error"
      ? "Payment could not be completed. Please retry the payment."
      : "Payment completed successfully.";

  return {
    status,
    title: status === "error" ? "Payment Failed" : "Payment Successful",
    message: messageValue || errorValue || fallbackMessage,
    reference: finalReference,
    shouldClearQueryParams: hasPaymentParams,
    shouldClearPendingContext: Boolean(pendingContext),
  };
};

const UserManagement = () => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [toggleModal, setToggleModal] = useState({
    open: false,
    client: null,
  });
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activeSubscriptions: 0,
    monthlyRevenue: "$0",
    payslipsUsed: "0",
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    client: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    status: "success",
    title: "",
    message: "",
    reference: "",
  });
  const [statusFilter, setStatusFilter] = useState("All");

  const { showAlert } = useAlert();

  useEffect(() => {
    fetchClients();
  }, [page, rowsPerPage, searchText, statusFilter]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    const paymentPayload = buildPaymentModalPayload();
    if (!paymentPayload) return;

    setPaymentModal({
      open: true,
      status: paymentPayload.status,
      title: paymentPayload.title,
      message: paymentPayload.message,
      reference: paymentPayload.reference,
    });

    // Optimistically clear the "unsubscribed / expired" flags on a successful
    // payment so the global SubscriptionAlert hides immediately, without waiting
    // for the async profile refresh (getProfileView) to complete. The profile
    // fetch will overwrite these with the authoritative values shortly after.
    if (paymentPayload.status === "success") {
      localStorage.setItem("is_subscription", "true");
      localStorage.setItem("isExpired", "false");
      localStorage.setItem("isTrialExpired", "false");
      window.dispatchEvent(new Event("subscription_updated"));
    }

    if (paymentPayload.shouldClearPendingContext) {
      sessionStorage.removeItem(PAYSTACK_PAYMENT_CONTEXT_KEY);
    }

    if (paymentPayload.shouldClearQueryParams) {
      clearPaymentQueryParams();
    }
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const params = {
        page: 1,
        limit: 1000,
        search: searchText,
      };
      
      // Add filterBy parameter if not "All"
      if (statusFilter !== "All") {
        params.filterBy = statusFilter.toLowerCase();
      }
      
      const resultWithSearch = await subscriptionService.list(params);
      const allSubscriptions = normalizeListResponse(resultWithSearch);
      
      // Filter out deleted admins
      const validSubscriptions = allSubscriptions.filter((sub) => sub?.adminId?.isDeleted !== true);
      
      // Update total correctly based on valid remaining items
      setTotal(validSubscriptions.length);
      
      // Client-side pagination
      const startIndex = (page - 1) * rowsPerPage;
      const subscriptions = validSubscriptions.slice(startIndex, startIndex + rowsPerPage);
      
      let plansMap = {};
      try {
        const { planService } = await import("../../../api");
        const plansResult = await planService.list();
        const plans = normalizeListResponse(plansResult);
        plansMap = plans.reduce((acc, plan) => {
          acc[plan._id || plan.id] = plan;
          return acc;
        }, {});
      } catch (planError) {
        console.error("Error fetching plans:", planError);
      }
      
      const transformedClients = subscriptions.map((sub) => {
        const planIdFromSub = sub.planId && typeof sub.planId === "object" ? sub.planId._id : sub.planId;
        const plan = plansMap[planIdFromSub] || sub.planId || {};
        const company = sub.companyId || {};
        const admin = sub.adminId || {};
        const adminIsActive =
          typeof admin?.isActive === "boolean"
            ? admin.isActive
            : sub.isActive !== false;

        // Determine status from the subscription's own lifecycle field —
        // the same field the "Filter By" menu already queries via
        // `filterBy` (active/inactive/blocked/trial/expired) and the same
        // record whose `statusModifiedAt` drives the tooltip below. Falls
        // back to the admin account's active/blocked state only when a
        // subscription record has no status of its own.
        const rawSubscriptionStatus = sub?.status || sub?.subscriptionStatus;
        let clientStatus;
        if (rawSubscriptionStatus) {
          clientStatus =
            String(rawSubscriptionStatus).charAt(0).toUpperCase() +
            String(rawSubscriptionStatus).slice(1).toLowerCase();
        } else {
          clientStatus = "Active";
          if (!adminIsActive) {
            clientStatus = admin?.isBlocked ? "Blocked" : "Inactive";
          }
        }
        
        const formatDate = (dateString) => {
          if (!dateString) return "";
          try {
            return sharedFormatDate(dateString);
          } catch {
            return dateString;
          }
        };

        // Get modified date - when status was last changed
        const modifiedDate = admin?.updatedAt || admin?.lastModified || admin?.modifiedAt || 
                            sub?.updatedAt || sub?.lastModified || sub?.modifiedAt ||
                            company?.updatedAt || new Date().toISOString();
        
        return {
          id: sub._id || sub.id,
          company: company.companyName || "",
          country: company.country || "",
          plan: plan?.planName || "",
          isActive: adminIsActive,
          status: clientStatus,
          employees: `${sub?.employeeCount || 0} / ${sub?.employeeLimit || plan?.employeeLimit || 0}`,
          payslipsUsed: `${sub?.payslipCount || 0} / ${sub?.payslipLimit || plan?.payslipLimit || 0}`,
          nextBilling: formatDate(sub.nextBillingDate || sub.date),
          modifiedDate: modifiedDate,
          updatedAt: modifiedDate,
          // New display fields from API
          joinDate: sub.joinDate || sub.createdAt || company.createdAt,
          lastLogin: admin.lastLogin || null,
          lastActivity: sub.lastActivity || admin.lastActivity || sub.updatedAt,
          currentPackage: plan?.planName || "N/A",
          packageBalance: sub.packageBalance || 0,
          subscription: sub,
          companyData: company,
          adminData: admin,
          planData: plan,
        };
      });
      
      setClients(transformedClients);
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error fetching subscriptions", "error");
    }
  };

  const fetchMetrics = async (filters = {}) => {
    try {
      const result = await subscriptionService.stats(filters);
      if ((result?.success === true || result?.status === "success") && result?.data) {
        setMetrics({
          totalClients: result.data.customers || 0,
          activeSubscriptions: result.data.activeSubscriptions ?? 0,
          monthlyRevenue: `$${(result.data.revenue || 0).toLocaleString()}`,
          payslipsUsed: String(result.data.payslipsGenerated || 0),
        });
        return;
      }

      // Fallback to existing summary API if stats is unavailable.
      const summaryResult = await subscriptionService.summary();
      if (summaryResult?.success !== false) {
        const summary = summaryResult?.data || summaryResult;
        const totalAdmins = summary.totalAdmins;
        const activeSubsFromApi = summary.activeSubscriptions;
        const totalPayslipsCreated = summary.totalPayslipsCreated;
        const activeCountFallback = [];

        setMetrics({
          totalClients: (typeof totalAdmins === "number" ? totalAdmins : undefined) ??
                        summary.totalClients ??
                        summary.totalSubscriptions ??
                        0,
          activeSubscriptions: (typeof activeSubsFromApi === "number" ? activeSubsFromApi : undefined) ??
                               activeCountFallback.length ??
                               0,
          monthlyRevenue: "$0",
          payslipsUsed: (typeof totalPayslipsCreated === "number"
            ? String(totalPayslipsCreated)
            : summary.payslipsUsed || "0"),
        });
      } else {
        setMetrics({
          totalClients: 0,
          activeSubscriptions: 0,
          monthlyRevenue: "$0",
          payslipsUsed: "0",
        });
      }
    } catch (error) {
      setMetrics({
        totalClients: 0,
        activeSubscriptions: 0,
        monthlyRevenue: "$0",
        payslipsUsed: "0",
      });
    }
  };

  const handleExport = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await subscriptionService.export();

      if (!result || result?.success === false) {
        showAlert(result?.message || "Failed to export subscriptions", "error");
        LoaderHelper.loaderStatus(false);
        return;
      }

      const blob = result instanceof Blob ? result : new Blob([result], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "subscriptions_export.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error exporting subscriptions", "error");
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentModal((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleSetRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearchText(String(value || "").trim());
    setPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleOpenToggleModal = (client) => {
    setToggleModal({
      open: true,
      client,
    });
  };

  const handleCloseToggleModal = () => {
    setToggleModal({
      open: false,
      client: null,
    });
  };

  const handleToggleClientStatus = async () => {
    const targetClient = toggleModal.client;
    if (!targetClient?.id) return;

    const currentlyActive =
      typeof targetClient?.adminData?.isActive === "boolean"
        ? targetClient.adminData.isActive
        : targetClient?.isActive !== false;
    const nextActiveState = !currentlyActive;
    const adminId =
      targetClient?.adminData?._id ||
      targetClient?.adminData?.id ||
      targetClient?.subscription?.adminId?._id ||
      targetClient?.subscription?.adminId?.id ||
      targetClient?.subscription?.adminId;

    LoaderHelper.loaderStatus(true);
    try {
      const result = await subscriptionService.blockByAdmin(adminId, {
        isActive: nextActiveState,
      });

      if (result?.success !== false) {
        showAlert(
          result?.message || `Client ${nextActiveState ? "unblocked" : "blocked"} successfully`,
          "success"
        );
        handleCloseToggleModal();
        await fetchClients();
        await fetchMetrics();
      } else {
        showAlert(result?.message || "Failed to update client status", "error");
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error updating client status", "error");
    }
  };

  const handleOpenDeleteModal = (client) => {
    setDeleteModal({
      open: true,
      client,
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      open: false,
      client: null,
    });
  };

  const handleConfirmDeleteClient = async () => {
    const targetClient = deleteModal.client;
    if (!targetClient?.id) return;

    const adminId =
      targetClient?.adminData?._id ||
      targetClient?.adminData?.id ||
      targetClient?.subscription?.adminId?._id ||
      targetClient?.subscription?.adminId?.id ||
      targetClient?.subscription?.adminId;

    if (!adminId) {
      showAlert("Unable to identify client for deletion", "error");
      return;
    }

    setDeleteLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await subscriptionService.deleteByAdmin(adminId);
      if (result?.success !== false) {
        showAlert(result?.message || "Client deleted successfully", "success");
        handleCloseDeleteModal();
        await fetchClients();
        await fetchMetrics();
      } else {
        showAlert(result?.message || "Failed to delete client", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Error deleting client", "error");
    } finally {
      setDeleteLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleApplyBusinessFilters = async (filters) => {
    // Filter out empty values
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value && value.trim() !== "") {
        acc[key] = value.trim();
      }
      return acc;
    }, {});

    await fetchMetrics(cleanFilters);
  };

  const handleResetBusinessFilters = async () => {
    await fetchMetrics({});
  };

  return (
    <UserManagementPage
      clients={clients}
      page={page}
      total={total}
      setPage={setPage}
      setRowsPerPage={handleSetRowsPerPage}
      rowsPerPage={rowsPerPage}
      searchText={searchText}
      onSearchChange={handleSearch}
      onToggleBlock={handleOpenToggleModal}
      toggleModalOpen={toggleModal.open}
      toggleTargetClient={toggleModal.client}
      onCloseToggleModal={handleCloseToggleModal}
      onConfirmToggleModal={handleToggleClientStatus}
      metrics={metrics}
      loading={loading}
      onExport={handleExport}
      paymentModalOpen={paymentModal.open}
      paymentModalStatus={paymentModal.status}
      paymentModalTitle={paymentModal.title}
      paymentModalMessage={paymentModal.message}
      paymentReference={paymentModal.reference}
      onClosePaymentModal={handleClosePaymentModal}
      deleteModalOpen={deleteModal.open}
      deleteTargetClient={deleteModal.client}
      onDeleteClient={handleOpenDeleteModal}
      onCloseDeleteModal={handleCloseDeleteModal}
      onConfirmDeleteModal={handleConfirmDeleteClient}
      deleteLoading={deleteLoading}
      statusFilter={statusFilter}
      onStatusFilterChange={handleStatusFilterChange}
      onApplyBusinessFilters={handleApplyBusinessFilters}
      onResetBusinessFilters={handleResetBusinessFilters}
    />
  );
};

export default UserManagement;
